import React, { useState, useMemo } from 'react';
import type { MoFamily } from './types';
import { FamilyCard, HierarchyIcon } from '../../components/FamilyCard';
import { BarterPairCard } from '../../components/BarterPairCard';

type GroupedItem =
 | { kind: 'single'; family: MoFamily }
 | { kind: 'pair'; primary: MoFamily; secondary: MoFamily };

/**
 * 체결된 바터의 자기-owner 두 family (a + with-b) 는 한 카드 (BarterPairCard) 로 묶고,
 * 나머지는 일반 FamilyCard 로. createdAt DESC 정렬 (생성 시점 늦은 순) 후
 * 그룹핑할 때 같은 barter_pair_id 의 자기-owner 짝을 발견하면 한 그룹으로.
 */
const groupFamilies = (families: MoFamily[]): GroupedItem[] => {
 const base = [...families].sort((a, b) =>
  (b.createdAt || '').localeCompare(a.createdAt || ''),
 );
 const placed = new Set<string>();
 const result: GroupedItem[] = [];
 for (const f of base) {
  if (placed.has(f.moFamilyHash)) continue;
  // 체결 바터 + 같은 owner 인 짝 family 찾기
  if (f.familyType === 'barter' && f.barterPairId) {
   const pair = base.find(x =>
    x.moFamilyHash !== f.moFamilyHash &&
    x.barterPairId === f.barterPairId &&
    !placed.has(x.moFamilyHash) &&
    (x.isOwner || x.role === 'owner') === (f.isOwner || f.role === 'owner'),
   );
   if (pair) {
    // primary = 더 오래된 (사용자가 먼저 만든 a), secondary = 자동 생성 with-b
    const [primary, secondary] = (f.createdAt || '') < (pair.createdAt || '')
     ? [f, pair]
     : [pair, f];
    result.push({ kind: 'pair', primary, secondary });
    placed.add(f.moFamilyHash);
    placed.add(pair.moFamilyHash);
    continue;
   }
  }
  result.push({ kind: 'single', family: f });
  placed.add(f.moFamilyHash);
 }
 return result;
};

interface FamilyListViewProps {
 families: MoFamily[];
 isLoading: boolean;
 error: string | null;
 onNavigate: (path: string) => void;
 onCreateFamily: (name: string, description: string) => Promise<void>;
 onCreateDefaultFamily: () => Promise<void>;
}

export const FamilyListView: React.FC<FamilyListViewProps> = ({
 families,
 isLoading,
 error,
 onNavigate,
 onCreateFamily,
 onCreateDefaultFamily,
}) => {
 const [showCreateModal, setShowCreateModal] = useState(false);
 const [newFamilyName, setNewFamilyName] = useState('');
 const [newFamilyDesc, setNewFamilyDesc] = useState('');

 const hasDefaultFamily = families.some(f =>
 f.description?.includes('자동 생성') ||
 f.description?.includes('기본 모패밀리') ||
 f.name?.includes('기본 모패밀리') ||
 f.maxMembers === 1
 );

 const groupedFamilies = useMemo(() => groupFamilies(families), [families]);

 const handleCreate = async () => {
 if (!newFamilyName.trim()) return;
 await onCreateFamily(newFamilyName.trim(), newFamilyDesc);
 setShowCreateModal(false);
 setNewFamilyName('');
 setNewFamilyDesc('');
 };

 return (
 <div>
 <div className="flex flex-wrap justify-between items-start gap-3 mb-2">
 <h1 className="text-2xl md:text-3xl font-bold text-gray-800 flex items-center gap-2">
 내 모패밀리
 <img src="/static/MoFamily.png" alt="MoFamily" className="h-[1.4em] w-auto object-contain" />
 </h1>
 <div className="flex gap-2">
 {!hasDefaultFamily && (
 <button
 onClick={onCreateDefaultFamily}
 className="bg-green-500 hover:bg-green-600 text-white px-4 py-2"
 id="createDefaultBtn"
 >
 기본 모패밀리 생성
 </button>
 )}
 <button
 onClick={() => setShowCreateModal(true)}
 className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2"
 >
 + 새 모패밀리
 </button>
 </div>
 </div>
 <p className="text-sm text-gray-500 leading-relaxed mb-6">
 여러 패밀리가 모여 더 큰 패밀리를 이룰 수 있습니다. 광고주는 스크린 없이 패밀리를 만든 뒤,
 스크린을 가진 다른 패밀리를 초대해 송출 네트워크를 구성할 수 있습니다.
 </p>

 {error && (
 <div className="bg-red-50 text-red-800 p-4 mb-4">
 {error}
 </div>
 )}

 {isLoading && families.length === 0 ? (
 <div className="text-center py-8">
 <div className="inline-block animate-spin h-8 w-8 rounded-full border-4 border-gray-200 border-t-gray-900"></div>
 <p className="mt-2 text-gray-600">로딩 중...</p>
 </div>
 ) : families.length === 0 ? (
 <div className="bg-white p-12 text-center">
 <svg className="w-24 h-24 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
 </svg>
 <h3 className="text-xl font-medium mb-2">아직 모패밀리가 없습니다</h3>
 <p className="text-gray-500 mb-6">첫 번째 모패밀리를 만들어 협업을 시작하세요</p>
 <button
 onClick={() => setShowCreateModal(true)}
 className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-3"
 >
 첫 모패밀리 만들기
 </button>
 </div>
 ) : (
 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-[repeat(auto-fill,minmax(380px,1fr))] gap-4">
 {groupedFamilies.map((g) =>
  g.kind === 'pair' ? (
   <BarterPairCard
    key={g.primary.moFamilyHash}
    primary={g.primary}
    secondary={g.secondary}
    onDetail={(hash) => onNavigate(`/mofamily/${hash}`)}
   />
  ) : (
   <FamilyCard
    key={g.family.moFamilyHash}
    family={g.family}
    onDetail={() => onNavigate(`/mofamily/${g.family.moFamilyHash}`)}
   />
  )
 )}
 </div>
 )}

 {/* 표시 안내 (범례) — 처음 사용자도 카드 배지/왼쪽 색 띠 의미를 알 수 있게 */}
 {families.length > 0 && (
 <div className="mt-8 border-t border-gray-200 pt-4">
 <h4 className="text-xs font-semibold text-gray-500 mb-2">표시 안내 — 카드의 배지와 왼쪽 색 띠</h4>
 <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
 {[
 { text: '영상 넣기 가능', cls: 'bg-green-100 text-green-800 border border-green-200', desc: '내가 영상을 넣을 수 있는 슬롯이 있는 모패밀리' },
 { text: '전체 위임됨', cls: 'bg-orange-100 text-orange-800 border border-orange-200', desc: '모든 슬롯을 다른 모패밀리에 위임해 영상을 넣을 수 없음' },
 { text: '슬롯 없음', cls: 'bg-gray-100 text-gray-600 border border-gray-200', desc: '자체 슬롯이 없는 모패밀리 (위임·광고주용)' },
 { text: '바터용', cls: 'bg-purple-100 text-purple-800 border border-purple-200', desc: '슬롯을 1:1 교환하는 바터 전용 모패밀리' },
 ].map((item) => (
 <div key={item.text} className="flex items-center gap-2 text-xs text-gray-600">
 <span className={`px-1.5 py-0.5 font-normal whitespace-nowrap ${item.cls}`}>{item.text}</span>
 <span>{item.desc}</span>
 </div>
 ))}
 <div className="flex items-center gap-2 text-xs text-gray-600">
 <span
  className="w-8 h-5 border border-gray-300 shrink-0"
  style={{ backgroundImage: 'repeating-linear-gradient(45deg, rgba(96,125,139,0.14) 0, rgba(96,125,139,0.14) 5px, transparent 5px, transparent 12px)' }}
 />
 <span>사선 배경 — 내가 제어할 수 있는 슬롯이 없는 모패밀리</span>
 </div>
 </div>
 <h4 className="text-xs font-semibold text-gray-500 mb-2 mt-4">계층 위치 — 슬롯 위임 트리에서의 자리</h4>
 <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
 {[
 { pos: 'top' as const, label: '최상위(루트)', desc: '위임을 받기만 하는 가장 부모' },
 { pos: 'middle' as const, label: '중간 계층', desc: '위·아래 모두 연결' },
 { pos: 'bottom' as const, label: '최하위(막내)', desc: '상위에만 위임' },
 ].map((item) => (
 <div key={item.pos} className="flex items-center gap-2 text-xs text-gray-600">
 <HierarchyIcon pos={item.pos} className="shrink-0" />
 <span className="font-medium whitespace-nowrap">{item.label}</span>
 <span className="text-gray-400">— {item.desc}</span>
 </div>
 ))}
 </div>
 </div>
 )}

 {/* Create Modal */}
 {showCreateModal && (
 <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
 <div className="bg-white p-6 max-w-md w-full">
 <h2 className="text-xl font-bold mb-4">새 모패밀리 만들기</h2>
 <form onSubmit={(e) => { e.preventDefault(); handleCreate(); }}>
 <div className="mb-4">
 <label className="block text-sm font-medium text-gray-700 mb-2">
 모패밀리 이름 <span className="text-red-500">*</span>
 </label>
 <input
 type="text"
 value={newFamilyName}
 onChange={(e) => setNewFamilyName(e.target.value)}
 required
 maxLength={100}
 placeholder="우리 가족"
 className="w-full px-3 py-2 border focus:outline-none focus:ring-2 focus:ring-blue-500"
 />
 </div>
 <div className="mb-6">
 <label className="block text-sm font-medium text-gray-700 mb-2">
 설명
 </label>
 <textarea
 value={newFamilyDesc}
 onChange={(e) => setNewFamilyDesc(e.target.value)}
 rows={3}
 maxLength={500}
 placeholder="모패밀리 설명을 입력하세요"
 className="w-full px-3 py-2 border focus:outline-none focus:ring-2 focus:ring-blue-500"
 />
 </div>
 <div className="flex gap-3">
 <button
 type="submit"
 className="flex-1 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2"
 >
 생성
 </button>
 <button
 type="button"
 onClick={() => {
 setShowCreateModal(false);
 setNewFamilyName('');
 setNewFamilyDesc('');
 }}
 className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2"
 >
 취소
 </button>
 </div>
 </form>
 </div>
 </div>
 )}
 </div>
 );
};
